import { z } from 'zod';
import { KEEPER_READ_TOOLS } from './operations.js';
/**
 * A twin's task (Amendment 0011, ADR-0139): the plan a person confirms, and
 * what each tool it names needs before it can run.
 *
 * The plan is composed by a model from the person's own words, and nothing a
 * model writes reaches a destination: every tool is a manifest id the
 * platform resolved against its own library (a model's invented id is
 * dropped before the person sees it), and every web query is shown to the
 * person, word for word, before it can be sent anywhere.
 */
export const TOOL_ID = /^[a-z][a-z_]*\.[a-z][a-z_0-9]*$/;
const toolId = z.string().regex(TOOL_ID, 'a manifest id, <department>.<name>');
/** A line a person reads: one line, no control characters. */
const line = (max) => z
    .string()
    .trim()
    .min(1)
    .max(max)
    .regex(/^[^\u0000-\u001f\u007f]+$/, 'one line of text');
/** At most this many tool calls in one task, whatever the plan says. */
/**
 * The task runner currently knows how to execute these research surfaces.
 * A ledger manifest alone does not supply argument mapping or a result reader
 * for an arbitrary HTTP or MCP tool. A plan offers only these, and the admin
 * door grants only these, until a typed task executor is implemented for
 * another; a person asks for any other tool as a request, not a task.
 */
export function twinTaskCanExecute(toolId, department, procedure) {
    // A superadmin's standing assignment (Amendment 0013, ADR-0172) is granted
    // the catalogue's reads, which the operations runner executes; nothing
    // else may be granted to it, and no ordinary task is granted those.
    if (procedure === 'twin_operations')
        return department === 'tech' && KEEPER_READ_TOOLS.includes(toolId);
    return toolId === 'research.web_grounded'
        || toolId === 'research.page_markdown';
}
export const TWIN_TASK_MAX_CALLS = 12;
/** A plan names at most this many tools. */
export const TWIN_TASK_MAX_TOOLS = 12;
/** And at most this many web searches, each shown to the person first. */
export const TWIN_TASK_MAX_QUERIES = 4;
/** Each source's page is read back at most this many times per task. */
export const TWIN_TASK_MAX_VERIFY = 5;
export const twinTaskStepSchema = z
    .object({
    toolId,
    /** Why this tool, in the plan's words — shown, never executed. */
    purpose: line(200),
})
    .strict();
export const twinTaskPlanSchema = z
    .object({
    title: line(200),
    /** What is being looked into or prepared. */
    subject: line(500),
    /** Complete confirmed human instructions, independent of the short plan summary. */
    originalRequest: z.string().min(1).optional(),
    /** What the person gets back. */
    output: line(300),
    /** The literal public web queries, each one the person reads before confirming. */
    queries: z.array(line(300)).max(TWIN_TASK_MAX_QUERIES),
    steps: z.array(twinTaskStepSchema).min(1).max(TWIN_TASK_MAX_TOOLS),
    /** Immutable material snapshots in the person's chat, chosen before confirmation. */
    materials: z.array(z.object({
        id: z.string().uuid(),
        filename: line(120),
        source: z.enum(['upload', 'drive']),
    }).strict()).max(5).optional(),
    maxCalls: z.number().int().min(1).max(TWIN_TASK_MAX_CALLS),
})
    .strict();
/**
 * Where one tool stands for this task, as the platform found it:
 *
 * - `usable`: the twin holds it, or its record earns it for this task.
 * - `needs_person`: it exists, signed and admitted; a superadmin grants it
 *   to this task, and the task waits for that.
 * - `unavailable`: something must be set up first — a manifest written and
 *   signed, an admission, a connection key, a transfer mechanism. The
 *   remedy says which.
 */
export const TOOL_STANDING = ['usable', 'needs_person', 'unavailable'];
/** Five unbroken grants earn the sixth (Amendment 0011, the floor's point 3). */
export const EARNED_AFTER = 5;
/** An earned grant lasts at most this long, and never past its task. */
export const EARNED_GRANT_DAYS = 14;
/** A superadmin's task grant lasts at most this long. */
export const SUPERVISED_GRANT_MAX_DAYS = 30;
/** The states a task's need for one tool can be in (migration 0112). */
export const TASK_CAPABILITY_STATES = ['held', 'requested', 'granted', 'refused', 'unavailable'];
/** Where a blocker notice goes, beyond the header and the side chat. */
export const NOTIFY_CHANNEL_KINDS = ['google_chat', 'telegram'];
/** The target's shape, the same one migration 0112 checks. */
export const NOTIFY_TARGET = {
    google_chat: /^[A-Za-z0-9_-]{6,64}$/,
    telegram: /^-?[0-9]{3,20}$/,
};
/** The two notice tools, as Kai (Executive) — the recipients are the channels a superadmin added, never a model's. */
export const NOTICE_TOOLS = { google_chat: 'executive.chat_notice', telegram: 'executive.telegram_notice' };
//# sourceMappingURL=twin-task.js.map